import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import { useAtomValue } from "jotai";
import { Button, Title } from "@common/index";
import { ComponentColor } from "@common/types";
import { ROUTES } from "@routing/routes";
import { useProductApi } from "@/hooks/apiHooks/useProductApi";
import { isProcessingProductsAtom } from "@/store/productAtoms";
import { LegalClassType } from "@models/product";
import { wasReported } from "@utils/apiFailure";
import { ProductForm } from "../_components/ProductForm";
import { ProductCoaField } from "../_components/ProductCoaField";
import { productCoaError } from "../_helpers/productCoaFile";
import {
    productFormSchema,
    toNumber,
    type ProductFormData,
} from "../_helpers/productForm";
import {
    SAVE_BLOCKED_BY_CATEGORIES,
    useProductFormLookups,
} from "../_helpers/useProductFormLookups";
import { NewProductImages } from "./_components/NewProductImages";
import { usePickedImages } from "./_helpers/usePickedImages";

export const NewProductPage = () => {
    const navigate = useNavigate();
    const { createProduct } = useProductApi();
    const { categories, categoriesState, retryCategories } =
        useProductFormLookups();
    const isProcessing = useAtomValue(isProcessingProductsAtom);
    // Kept through a failed create, so a retry sends them again.
    const pickedImages = usePickedImages();
    // #476: the optional COA, held like the images until the create sends
    // it. A refused pick keeps the one already chosen.
    const [coa, setCoa] = useState<File | null>(null);
    const [coaRefusal, setCoaRefusal] = useState<string | null>(null);
    // Loading or failed with nothing to pick from: the required category
    // cannot be chosen, so neither save is offered until the list is there.
    const categoriesUnavailable = categoriesState !== "ready";

    const {
        register,
        handleSubmit,
        reset,
        watch,
        setFocus,
        formState: { errors },
    } = useForm<ProductFormData>({
        resolver: zodResolver(productFormSchema),
        defaultValues: { categoryId: "", legalClasses: [] },
    });

    // Name, Brand, Description and UPC are required by the schema, so each is
    // a non-blank string here. EAN and Manufacturer SKU are optional: a blank
    // one trims to "", which createProduct does not send, and the API stores
    // a missing one as "".
    //
    // Resolves false when the create failed in a way useApi has already shown
    // the admin (an HTTP or network failure it toasted, or an expired session
    // it warned about before redirecting to sign in). That failure is not
    // rethrown out of the submit handler, where nothing would catch it; the
    // form and the chosen images stay as they were for a retry. Any failure
    // useApi did not report still throws.
    //
    // No price or cost is sent (P1/P2): the API derives both from the vendor
    // offers.
    const submit = async (values: ProductFormData): Promise<boolean> => {
        try {
            await createProduct({
                name: values.name.trim(),
                brand: values.brand.trim(),
                categoryId: Number(values.categoryId),
                description: values.description.trim(),
                upc: values.upc.trim(),
                ean: values.ean?.trim(),
                manufacturerSku: values.manufacturerSku?.trim(),
                msrp: toNumber(values.msrp),
                discountAllowed: values.discountAllowed ?? false,
                serialized: false,
                legalClasses: values.legalClasses.map(
                    (value) => Number(value) as LegalClassType,
                ),
                images: pickedImages.images.map((image) => image.file),
                coa: coa ?? undefined,
                // TODO(backend): customSku — no API support yet.
            });
            return true;
        } catch (error) {
            if (wasReported(error)) {
                return false;
            }
            throw error;
        }
    };

    const pickCoa = (file: File) => {
        const refusal = productCoaError(file);

        if (!refusal) {
            setCoa(file);
        }
        setCoaRefusal(refusal);
    };

    const onSave = handleSubmit(async (values) => {
        if (await submit(values)) {
            navigate(ROUTES.ADMIN_PRODUCTS);
        }
    });

    const onSaveAndAddAnother = handleSubmit(async (values) => {
        if (!(await submit(values))) {
            return;
        }
        reset();
        pickedImages.clear();
        setCoa(null);
        setCoaRefusal(null);
        // Return focus to Name for the next entry. Defer past the reset()
        // re-render, which otherwise clears a synchronous setFocus.
        setTimeout(() => setFocus("name"), 0);
    });

    return (
        <div className="px-10 text-neutral">
            <Title>Add New Product</Title>

            <form noValidate onSubmit={onSave}>
                <ProductForm
                    register={register}
                    errors={errors}
                    watch={watch}
                    categories={categories}
                    imageSlot={
                        <NewProductImages
                            images={pickedImages.images}
                            refusals={pickedImages.refusals}
                            onAdd={pickedImages.add}
                            onRemove={pickedImages.remove}
                            disabled={isProcessing}
                        />
                    }
                    coaSlot={
                        <ProductCoaField
                            fileName={coa?.name ?? null}
                            onPick={pickCoa}
                            onRemove={() => {
                                setCoa(null);
                                setCoaRefusal(null);
                            }}
                            disabled={isProcessing}
                            notice={
                                coaRefusal
                                    ? { tone: "error", text: coaRefusal }
                                    : null
                            }
                        />
                    }
                    categoriesState={categoriesState}
                    onRetryCategories={retryCategories}
                />

                {categoriesState === "failed" && (
                    <p className="text-sm text-error text-center mt-6">
                        {SAVE_BLOCKED_BY_CATEGORIES}
                    </p>
                )}

                <div className="flex justify-center gap-4 my-8">
                    <Button
                        type="submit"
                        color={ComponentColor.Primary}
                        loading={isProcessing}
                        disabled={categoriesUnavailable}
                    >
                        Save
                    </Button>
                    <Button
                        type="button"
                        color={ComponentColor.Primary}
                        outline
                        loading={isProcessing}
                        disabled={categoriesUnavailable}
                        onClick={onSaveAndAddAnother}
                    >
                        Save + Add Another
                    </Button>
                    <Button
                        type="button"
                        color={ComponentColor.Ghost}
                        className="text-neutral"
                        disabled={isProcessing}
                        onClick={() => navigate(-1)}
                    >
                        Cancel
                    </Button>
                </div>
            </form>
        </div>
    );
};
